// ==================== 模型配置 ====================
// DeepSeek V4-Flash（2026 年 5 月基于六款模型对比调研结论切换）
const DIRECT_API_URL = 'https://api.deepseek.com/chat/completions'

// ==================== 流式调用（代理优先） ====================
/**
 * 尝试通过后端代理调用 DeepSeek API（线上环境，Key 不暴露给浏览器）。
 * 失败时静默返回 null，由上层决定是否降级。
 */
async function tryProxyCall(systemPrompt: string, userMessage: string, maxTokens?: number): Promise<Response | null> {
  try {
    const payload: Record<string, unknown> = { systemPrompt, userMessage }
    if (maxTokens) payload.maxTokens = maxTokens

    const res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res.ok) return res

    // 代理返回了业务错误（如 503），不降级，直接报错让上层处理
    const errorData = await res.json().catch(() => ({}))
    const serverMsg = (errorData as { error?: string }).error || ''
    throw new Error(`请求失败 (${res.status})${serverMsg ? '：' + serverMsg : ''}`)
  } catch {
    return null
  }
}

/**
 * 降级方案：前端直接用环境变量中的 Key 调用 DeepSeek API（仅本地开发）。
 */
async function* directCall(systemPrompt: string, userMessage: string, maxTokens?: number): AsyncGenerator<string> {
  const apiKey = import.meta.env.VITE_API_KEY
  if (!apiKey) {
    throw new Error('API Key 未配置。线上请确保 Cloudflare 环境变量已设置，本地请配置 VITE_API_KEY')
  }

  const response = await fetch(DIRECT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: 'deepseek-v4-flash',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage },
      ],
      temperature: 0.3,
      stream: true,
      ...(maxTokens ? { max_tokens: maxTokens } : {}),
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const serverMsg = (errorData as { error?: { message?: string } }).error?.message || ''
    throw new Error(`请求失败 (${response.status})${serverMsg ? '：' + serverMsg : ''}`)
  }

  yield* streamSSEResponse(response)
}

/** 解析 Server-Sent Events 响应，逐个 yield 文本块 */
async function* streamSSEResponse(response: Response): AsyncGenerator<string> {
  const reader = response.body!.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed || !trimmed.startsWith('data: ')) continue
      const jsonStr = trimmed.slice(6)
      if (jsonStr === '[DONE]') continue

      try {
        const json: { choices?: [{ delta?: { content?: string } }] } = JSON.parse(jsonStr)
        const delta = json.choices?.[0]?.delta?.content
        if (delta) yield delta
      } catch {
        // 忽略解析失败的行
      }
    }
  }
}

// ==================== 通用流式调用 ====================
/**
 * 使用 Server-Sent Events 流式调用 DeepSeek API，返回异步迭代器。
 * 三个 Agent 共用此函数，通过传入不同的 systemPrompt 和 userMessage 进行区分。
 *
 * 架构说明：
 * - 线上环境：前端 POST 到 /api/chat（Cloudflare Pages Function 代理），
 *   API Key 保存在 Cloudflare 环境变量中，不暴露给浏览器
 * - 本地开发：优先尝试代理，失败后直接通过 VITE_API_KEY 调用
 *
 * @param systemPrompt - 系统角色提示词
 * @param userMessage - 用户消息内容
 * @returns 每次 yield 一小段生成的文本
 */
export async function* streamChat(
  systemPrompt: string,
  userMessage: string,
  options?: { maxTokens?: number }
): AsyncGenerator<string> {
  // 临时调试用：通过 URL 参数模拟错误状态
  const urlParams = new URLSearchParams(window.location.search)
  const mock = urlParams.get('mock')
  if (mock === '503') throw new Error('请求失败 (503)')
  if (mock === '429') throw new Error('请求失败 (429)')

  // 优先尝试代理（线上环境）
  const proxyResponse = await tryProxyCall(systemPrompt, userMessage, options?.maxTokens)
  if (proxyResponse) {
    yield* streamSSEResponse(proxyResponse)
    return
  }

  // 代理不可用 → 降级到直接调用（本地开发）
  yield* directCall(systemPrompt, userMessage, options?.maxTokens)
}
